var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * @packageDocumentation
 * @module      CorMain.ts
 * @description The top-level Shui component for this application.
 * @copyright   Copyright © 2025 Siemens Healthineers. All rights reserved.
 */
import { css, customElement, html, internalProperty, LitElement } from 'lit-element';
import "./CorAccessBar";
import "./CorLogin";
import "./CorPageSystems";
import "./CorPageRoboticNetworks";
import "./CorPageAuditLogs";
import "./CorPageEMCTest";
import "./CorPageSystemTester";
import { CorNavBarTab, CorNavTabChangedEvent } from "./CorNavBar";
import LoggedInUser from "./LoggedInUser";
import CorThemeSelector from "./CorThemeSelector";
import EmcDataPoller from './emc/EmcDataPoller';
import { EmcDisplayFacade } from './emc/EmcDisplayFacade';
import { SystemTesterDisplayFacade } from './emc/SystemTesterDisplayFacade';
/** Styles that should be used on footers throughout the application. */
// noinspection CssInvalidHtmlTagReference
export const universalFooterStyles = css `
  div[slot="footer"] {
    display: flex;
    align-items: center;
  }

  sh-button:not(:first-child) {
    margin-left: 20px;
  }
`;
/**
 * Styles to apply to the list portion of the page.
 */
export const universalListStyles = css `
  sh-table-row[active][readonly] {
    background: rgba(var(--ui-1), var(--opacity-6)) !important; /* Same as active when not readonly */
  }
  
  sh-table-cell {
    height: auto; /* Without this, the System Status rides a little hither than the other cells. */
  }

  :host { 
    height: 80%; /* height selected 80% to prevent the filtered list going into scroll mode. */
  }
  
  .list-text-wrapper{
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 2rem;
  }
`;
/** Styles that should be used on Shui Cards throughout the application. */
export const universalCardStyles = css `
  @keyframes border-pulse {
    0% {
      border-width: 0;
    }
    25% {
      border-width: 3px;
    }
    99% {
      border-width: 3px;
    }
    100% {
      border-width: 0;
    }
  }

  [temp-highlight-action] {
    border-style: solid;
    border-color: rgba(0, 154, 56, 1); /* should be --functional-green but that has a bug: no rgba in definition. */
    border-radius: 5px;
    animation-name: border-pulse;
    animation-duration: 1s;
  }
`;
export const paddingStyles = css `
.paddingBottom24Pixel {
  padding-bottom:24px;
}
.paddingTop24Pixel {
  padding-top:24px;
}
.paddingAll{
  padding:8px 0px 24px 0px;
}
.paddingTop8pixel {
  padding-top:8px;
}
`;
/**
 * Styles to apply to all elements.
 */
export const universalStyles = css `
  :host(sh-modal) {
    pointer-events: none;
  }

  div.modal-wrapper {
    pointer-events: all;
  }
  `;
/**
 * The top-level Shui component for this application.
 */
let CorMain = class CorMain extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * The tab the user has selected. Initially set to RoboticNetworks but if the user is not authorized
         * to see that, we'll correct it in the render() method.
         */
        this.activeTab = CorNavBarTab.RoboticNetworks;
        this.isEmcEnabled = appConfig.isEmcEnabled;
    }
    /**
     * LitElement looks for a static property by this name. If it finds one, it applies these styles to the component.
     * See https://lit-element.polymer-project.org/guide/styles#expressions
     */
    static get styles() {
        // TODO Is there something better than setting an enormous width in px? width of 100% does not work.
        return css `
          ${universalStyles}

          cor-page-systems, cor-page-robotic-networks, cor-page-users, cor-page-audit-logs {
            width: 99999px;  
          }
        `;
    }
    get currentTheme() {
        return CorThemeSelector.getTheme();
    }
    get emcDataPoller() {
        return (true == this.isEmcEnabled) ? EmcDataPoller.getEmcDataPoller() : null;
    }
    get emcDisplayDataFacade() {
        return (true == this.isEmcEnabled) ? EmcDisplayFacade.getEmcDisplayFacade() : null;
    }
    get systemTesterDisplayFacade() {
        return SystemTesterDisplayFacade.getSystemTesterDisplayFacade();
    }
    /** Set the active tab in this class in response to a {@linkcode CorTabChangedEvent}. */
    setActiveTab(e) {
        this.activeTab = e.getNavTab();
    }
    handleLoginChange() {
        this.requestUpdate();
    }
    /**
     * A LitElement [lifecycle method](https://lit-element.polymer-project.org/guide/lifecycle), which it
     * inherits from the [Web Component Standard](https://developer.mozilla.org/en-US/docs/Web/Web_Components/Using_custom_elements#Using_the_lifecycle_callbacks),
     * that is invoked when this custom component is added to the DOM. In this case, it adds event listeners to the component.
     */
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener(CorNavTabChangedEvent.eventName, this.setActiveTab);
        if (this.emcDataPoller)
            this.emcDataPoller.poll();
        if (this.emcDisplayDataFacade)
            this.emcDisplayDataFacade.refresh();
        if (this.systemTesterDisplayFacade)
            this.systemTesterDisplayFacade.refresh();
    }
    /**
     * A LitElement [lifecycle method](https://lit-element.polymer-project.org/guide/lifecycle), which it
     * inherits from the [Web Component Standard](https://developer.mozilla.org/en-US/docs/Web/Web_Components/Using_custom_elements#Using_the_lifecycle_callbacks),
     * that is invoked when this custom component is added to the DOM. In this case, it removes event listeners from the component.
     */
    disconnectedCallback() {
        this.removeEventListener(CorNavTabChangedEvent.eventName, this.setActiveTab);
        super.disconnectedCallback();
    }
    /** Renders the component as part of LitElement's lifecycle. */
    render() {
        // activeTab was initialized to RoboticNetworks. However, once the user is logged in, we may discover
        // that he does not have access to that (because he's not a robotic network admin). In that case, he must
        // be an audit user and we should set the tab appropriately.
        if (!LoggedInUser.isRoboticNetworkAdmin() && this.activeTab == CorNavBarTab.RoboticNetworks) {
            this.activeTab = CorNavBarTab.AuditLogs;
        }
        let pageContents = html ``;
        // Each page (cor-page-systems, etc.) occupies slot="left" of the parent sh-page because that slot has
        // no margins. And then, each page also consists of an sh-page without access bar or nav bar.
        //
        // This is a work-around for a limitation of Lit Element, namely that there is no such thing
        // as a totalhly transparent wrapper for child components. For example we might like to encapsulate
        // everything about the Systems page in one component, cor-page-systems, and have that page consist
        // of a cor-systems-list that occupies the "left" slot of CorMain's sh-page, and a cor-system-card
        // that occupies CorMain's main content area. But that is impossible because the
        // cor-page-systems component that wraps cor-systems-list and cor-system-card gets in the way (is
        // not transparent). The workaround here is to make cor-page-systems consist of an sh-page
        // into whose slots cor-systems-list and cor-system-card can fit. Although this means we have one
        // sh-page nested inside another, which is a little strange, it does allow us to pick up the Shui-
        // designed margins properly. The outer sh-page supplies the access and nav bars and plugs
        // cor-page-systems into the left slot with no margin; the inner sh-page has no access or nav bar
        // and puts the list into the left slot (still with no margin) and the card in the main area.
        //
        // The benefit of this work-around is that the CorPageSystems.ts module can encapsulate everything
        // about the Systems page: its list and card components but also the custom events by which those
        // components communicate. Without that encapsulation, those details would leak into this CorMain
        // component where they do not belong.
        switch (this.activeTab) {
            case CorNavBarTab.RoboticNetworks:
                pageContents = html `
                        <cor-page-robotic-networks slot="left"></cor-page-robotic-networks>`;
                break;
            case CorNavBarTab.Systems:
                pageContents = html `
                        <cor-page-systems slot="left"></cor-page-systems>`;
                break;
            case CorNavBarTab.AuditLogs:
                pageContents = html `
                        <cor-page-audit-logs slot="left"></cor-page-audit-logs>`;
                break;
            case CorNavBarTab.EMCTest:
                pageContents = html `
                        <cor-emc-test></cor-emc-test>`;
                break;
            case CorNavBarTab.SystemTester:
                pageContents = html `
                        <cor-system-tester></cor-system-tester>`;
                break;
            default:
                break;
        }
        return html `
                <sh-page theme="${this.currentTheme}">
                    <cor-access-bar slot="access"></cor-access-bar>
                    <cor-nav-bar slot="nav" activeTab="${this.activeTab}"></cor-nav-bar>
                    ${pageContents}
                </sh-page>
            `;
    }
};
/**
 * Styles that should be used for the left pane when it contains a list of items (e.g., Robotic Networks),
 * throughout the application.
 */
// noinspection CssInvalidHtmlTagReference
CorMain.universalLeftPaneStyles = css `
      @keyframes border-pulse {
        0% {
          border-width: 0;
        }
        25% {
          border-width: 3px;
        }
        99% {
          border-width: 3px;
        }
        100% {
          border-width: 0;
        }
      }

      sh-table-row[lastAddedOrUpdated] {
        border-style: solid;
        border-color: rgba(0, 154, 56, 1); /* should be --functional-green but that has a bug: no rgba in definition. */
        border-radius: 5px;
        animation-name: border-pulse;
        animation-duration: 0.5s;
      }
    `;
__decorate([
    internalProperty()
], CorMain.prototype, "activeTab", void 0);
__decorate([
    internalProperty()
], CorMain.prototype, "currentTheme", null);
CorMain = __decorate([
    customElement('cor-main')
], CorMain);
export { CorMain };
//# sourceMappingURL=CorMain.js.map